import './workflow.js';
import { COUNTRY_PAGES_BY_SLUG } from './country-config.mjs';
import { submitContactRequest } from './contact-form-api.mjs';
import { submitTemplateSignup } from './template-form-api.mjs';

const appUrl = 'https://www.rankerd.com/app/#/login';

function trackEvent(name, properties = {}) {
  if (typeof window.rankerdAnalytics?.track === 'function') window.rankerdAnalytics.track(name, properties);
  window.dispatchEvent(new CustomEvent('rankerd:analytics', { detail: { name, properties } }));
}

let templateServerAnalyticsEnabled = (() => {
  try { return window.sessionStorage.getItem('rankerd:template-analytics-unsupported') !== '1'; } catch (_) { return true; }
})();

function templateAnalytics(name, properties = {}) {
  trackEvent(name, properties);
  if (!templateServerAnalyticsEnabled) return;
  const query = new URLSearchParams(window.location.search);
  const attribution = Object.fromEntries(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].map((key) => [key, query.get(key) || '']).filter(([, value]) => value));
  fetch('/api/public/index.php?route=v1/analytics/visitor-event', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event_name: name, page_path: window.location.pathname, referrer: document.referrer, properties: { ...attribution, ...properties } }), keepalive: true }).then((response) => {
    // A staggered static/API release can briefly leave the old allow-list in
    // place. Stop retrying unsupported new events for this browser session.
    if (response.status === 422) {
      templateServerAnalyticsEnabled = false;
      try { window.sessionStorage.setItem('rankerd:template-analytics-unsupported', '1'); } catch (_) {}
    }
  }).catch(() => {});
}

const links = [
  ['Product', '/for-tutors/'], ['Lesson Notes', '/lesson-snapshots/'], ['Parent Updates', '/parent-updates/'], ['Assignments', '/assignments/'], ['Teams', '/teams/'], ['Pricing', '/pricing/'],
  ['Resources', '/resources/'], ['About', '/about/'],
];

function current(path) {
  const pathname = window.location.pathname;
  return pathname === path || (path !== '/' && pathname.startsWith(path));
}

function header() {
  const pricingHref = '/pricing/#plan';
  const nav = links.map(([label, href]) => {
    const resolvedHref = label === 'Pricing' ? pricingHref : href;
    return `<a href="${resolvedHref}"${current(resolvedHref) ? ' aria-current="page"' : ''}>${label}</a>`;
  }).join('');
  return `<a class="skip-link" href="#main">Skip to content</a><header class="site-header" data-site-header>
    <div class="site-shell site-header-inner"><a class="site-logo" href="/" aria-label="Rankerd home"><img src="/assets/brand/rankerd-logo.png" width="40" height="40" alt="Rankerd" /><span aria-hidden="true">Rankerd</span></a>
      <nav class="site-nav" aria-label="Primary navigation">${nav}</nav>
      <div class="site-actions"><a class="site-contact" href="/contact/"${current('/contact/') ? ' aria-current="page"' : ''}>Contact</a><a class="site-login" href="${appUrl}">Log in</a><a class="site-btn" href="${appUrl}" data-marketing-trial data-source="header" data-placement="header">Start 14-day free trial</a><button class="site-menu-toggle" type="button" aria-label="Open navigation" aria-expanded="false" aria-controls="site-mobile-menu">☰</button></div>
    </div><nav id="site-mobile-menu" class="site-mobile-menu site-shell" aria-label="Mobile navigation">${nav}<a href="/contact/"${current('/contact/') ? ' aria-current="page"' : ''}>Contact</a><a class="site-btn" href="${appUrl}" data-marketing-trial data-source="mobile_header" data-placement="mobile_header">Start 14-day free trial</a></nav></header>`;
}

function footer() {
  const year = new Date().getFullYear();
  const pricingHref = '/pricing/#plan';
  return `<footer class="site-footer"><div class="site-shell"><div class="site-footer-grid"><section class="site-footer-brand"><a class="site-logo" href="/" aria-label="Rankerd home"><img src="/assets/brand/rankerd-logo.png" width="40" height="40" alt="Rankerd" /><span aria-hidden="true">Rankerd</span></a><p>Rankerd helps independent tutors and tutoring teams create parent-ready lesson updates, assignments and useful student insights.</p><div class="site-social"><p class="site-social-label">Follow Rankerd</p><div class="site-social-links"><a class="site-social-link" href="https://www.facebook.com/profile.php?id=61590372399412&sk=about" target="_blank" rel="noopener noreferrer" aria-label="Follow Rankerd on Facebook" data-footer-social="facebook"><svg viewBox="0 0 48 48" aria-hidden="true"><path fill="currentColor" d="M28 42V26h5.4l.8-6H28v-3.8c0-1.8.5-3 3.1-3H34V8.1c-1.3-.1-2.7-.2-4.1-.2-5.1 0-8.6 3.1-8.6 8.8V20h-5.5v6h5.5v16H28Z" /></svg></a><a class="site-social-link" href="https://www.instagram.com/app.rankerd/" target="_blank" rel="noopener noreferrer" aria-label="Follow Rankerd on Instagram" data-footer-social="instagram"><svg viewBox="0 0 48 48" aria-hidden="true"><rect x="8" y="8" width="32" height="32" rx="9" fill="none" stroke="currentColor" stroke-width="3.5" /><circle cx="24" cy="24" r="7.5" fill="none" stroke="currentColor" stroke-width="3.5" /><circle cx="33.5" cy="14.5" r="2.2" fill="currentColor" /></svg></a></div></div></section><section><h2>Product</h2><ul><li><a href="/lesson-snapshots/">Lesson Snapshots</a></li><li><a href="/parent-updates/">Parent Updates</a></li><li><a href="/assignments/">Assignments</a></li><li><a href="/student-progress-tracking/">Student Progress</a></li><li><a href="/teams/">Tutor Teams</a></li><li><a href="${pricingHref}">Pricing</a></li></ul></section><section><h2>Resources</h2><ul><li><a href="/how-it-works/">How It Works</a></li><li><a href="/resources/">Resources</a></li><li><a href="/faq/">FAQ</a></li></ul></section><section><h2>For tutors by region</h2><ul><li><a href="/uae/">UAE</a></li><li><a href="/singapore/">Singapore</a></li><li><a href="/us/">United States</a></li><li><a href="/malaysia/">Malaysia</a></li><li><a href="/uk/">United Kingdom</a></li><li><a href="/india/">India</a></li><li><a href="/qatar/">Qatar</a></li><li><a href="/saudi-arabia/">Saudi Arabia</a></li><li><a href="/middle-east/">Middle East</a></li></ul></section><section><h2>Company</h2><ul><li><a href="/contact/">Contact</a></li><li><a href="/privacy/">Privacy</a></li><li><a href="/terms/">Terms</a></li><li><a href="/cookies/">Cookies</a></li></ul></section></div><div class="site-footer-bottom"><span>© ${year} Rankerd. All rights reserved.</span><span>Rankerd drafts. You review before sending.</span></div></div></footer>`;
}

function marketingAnalytics(name, properties = {}) {
  trackEvent(name, properties);
  if (typeof window.fetch !== 'function') return;
  const query = new URLSearchParams(window.location.search);
  const attribution = Object.fromEntries(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].map((key) => [key, query.get(key) || '']).filter(([, value]) => value));
  fetch('/api/public/index.php?route=v1/analytics/visitor-event', { method:'POST', headers:{ 'Content-Type':'application/json' }, body:JSON.stringify({ event_name:name, page_path:window.location.pathname, referrer:document.referrer, country:document.body.dataset.countryPage === 'india' ? 'IN' : undefined, properties:{ ...attribution, ...properties } }), keepalive:true }).catch(() => {});
}

async function initLocalPricing() {
  const cards = [...document.querySelectorAll('[data-local-pricing]')];
  if (!cards.length || typeof fetch !== 'function') return;
  const country = cards[0].dataset.countryCode || 'US';
  try {
    const response = await fetch(`/api/public/index.php?route=v1/billing/public_pricing&country_code=${encodeURIComponent(country)}`, { headers: { Accept: 'application/json' }, credentials: 'omit' });
    if (!response.ok) return;
    const payload = await response.json();
    const pricing = payload?.data?.pricing_display;
    if (!pricing || Number(pricing.base_amount_usd ?? pricing.base_amount) !== 7.99) return;
    const currency = pricing.display_currency || 'USD';
    const amount = Number(pricing.display_amount);
    const estimate = Boolean(pricing.display_price_is_estimate) && currency !== 'USD' && Number.isFinite(amount);
    const formatted = estimate ? `${pricing.formatted_display || `${currency} ${amount.toFixed(2)}`} (approx.)` : 'US$7.99';
    const updatedAt = pricing.fx_fetched_at ? new Date(`${String(pricing.fx_fetched_at).replace(' ', 'T')}Z`) : null;
    const updatedLabel = updatedAt && !Number.isNaN(updatedAt.getTime()) ? ` Rate updated ${updatedAt.toLocaleDateString()}.${pricing.fx_is_stale ? ' The rate is stale.' : ''}` : '';
    for (const card of cards) {
      const price = card.querySelector('[data-local-price]');
      if (price) price.textContent = formatted;
      const note = card.querySelector('[data-local-price-note]');
      if (note) note.textContent = estimate
        ? `Approximate local estimate.${updatedLabel} Subscription is charged USD $7.99/month per organization.`
        : `USD $7.99/month per organization after the 14-day trial. Charged in USD.`;
      card.dataset.pricingCurrency = estimate ? currency : 'USD';
      card.dataset.pricingPrice = estimate ? amount.toFixed(2) : '7.99';
    }
  } catch (_) { /* Keep the USD reference when pricing or FX API is unavailable. */ }
}

function initMarketingAnalytics() {
  initLocalPricing();
  const page = window.location.pathname;
  const country = document.body.dataset.countryPage || '';
  document.querySelectorAll('a[href="https://www.rankerd.com/app/#/login"]:not([data-template-trial])').forEach((link) => link.addEventListener('click', () => marketingAnalytics('marketing_trial_clicked', { page, country: country || undefined, source: link.dataset.source || (country || 'website'), placement: link.dataset.placement || link.closest('section')?.className || 'cta' })));
  const pricing = document.querySelector('[data-pricing-currency][data-pricing-price]');
  if (pricing) marketingAnalytics('marketing_pricing_viewed', { page, displayed_currency: pricing.dataset.pricingCurrency, displayed_price: pricing.dataset.pricingPrice });
  document.querySelectorAll('[data-india-whatsapp]').forEach((link) => link.addEventListener('click', () => marketingAnalytics('india_whatsapp_clicked', { source:'india', placement:'floating_cta' })));
}

function initLegalNavigation() {
  const page = document.querySelector('.legal-page');
  if (!page) return;
  const links = [...page.querySelectorAll('.legal-toc a, .legal-mobile-toc a')];
  const sections = [...page.querySelectorAll('.legal-section[id]')];
  const setActive = (id) => links.forEach((link) => link.classList.toggle('is-active', link.getAttribute('href') === `#${id}`));
  links.forEach((link) => link.addEventListener('click', () => setActive(link.getAttribute('href').slice(1))));
  if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => { if (entry.isIntersecting) setActive(entry.target.id); }), { rootMargin: '-18% 0px -68% 0px', threshold: 0 });
    sections.forEach((section) => observer.observe(section));
  }
  if (window.location.hash) setActive(window.location.hash.slice(1));
}

function mountSharedChrome() {
  const oldNav = document.body.querySelector(':scope > nav');
  const oldFooter = document.body.querySelector(':scope > footer');
  if (oldNav) oldNav.outerHTML = header();
  else document.body.insertAdjacentHTML('afterbegin', header());
  if (oldFooter) oldFooter.outerHTML = footer();
  else document.body.insertAdjacentHTML('beforeend', footer());
  const toggle = document.querySelector('.site-menu-toggle');
  const menu = document.querySelector('.site-mobile-menu');
  const close = () => { menu?.classList.remove('is-open'); toggle?.setAttribute('aria-expanded', 'false'); };
  toggle?.addEventListener('click', () => { const open = !menu.classList.contains('is-open'); menu.classList.toggle('is-open', open); toggle.setAttribute('aria-expanded', String(open)); });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
  menu?.querySelectorAll('a').forEach((link) => link.addEventListener('click', close));
  document.querySelectorAll('[data-footer-social]').forEach((link) => link.addEventListener('click', () => trackEvent(`footer_${link.dataset.footerSocial}_clicked`)));
  window.addEventListener('scroll', () => document.querySelector('[data-site-header]')?.classList.toggle('is-scrolled', window.scrollY > 4), { passive:true });
}

function initMotionReveal() {
  const elements = document.querySelectorAll('.motion-reveal');
  if (!elements.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { elements.forEach((el) => el.classList.add('is-revealed')); return; }
  if (!('IntersectionObserver' in window)) { elements.forEach((el) => el.classList.add('is-revealed')); return; }
  const observer = new IntersectionObserver((entries) => entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); observer.unobserve(entry.target); } }), { threshold:.12, rootMargin:'0px 0px -5% 0px' });
  elements.forEach((el) => observer.observe(el));
}

function initSpeedProof() {
  const proof = document.querySelector('[data-speed-proof]');
  if (!proof) return;
  const reveal = () => proof.classList.add('is-revealed');
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) { reveal(); return; }
  const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
    if (entry.isIntersecting) { reveal(); observer.unobserve(entry.target); }
  }), { threshold: .18, rootMargin: '0px 0px -5% 0px' });
  observer.observe(proof);
}

function initTabs() {
  document.querySelectorAll('[data-tabs]').forEach((container) => {
    const tabs = [...container.querySelectorAll('[role="tab"]')];
    const panels = [...container.querySelectorAll('[role="tabpanel"]')];
    const activate = (tab) => {
      tabs.forEach((item) => { const active = item === tab; item.setAttribute('aria-selected', String(active)); item.tabIndex = active ? 0 : -1; });
      panels.forEach((panel) => { const active = panel.id === tab.getAttribute('aria-controls'); panel.classList.toggle('is-active', active); panel.hidden = !active; });
    };
    tabs.forEach((tab, index) => { tab.addEventListener('click', () => activate(tab)); tab.addEventListener('keydown', (event) => { if (!['ArrowRight','ArrowLeft','Home','End'].includes(event.key)) return; event.preventDefault(); const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length; tabs[next].focus(); activate(tabs[next]); }); });
  });
}

function initFaqs() { document.querySelectorAll('.faq-list details').forEach((item) => item.addEventListener('toggle', () => item.querySelector('summary')?.setAttribute('aria-expanded', String(item.open)))); }

function initFaqPage() {
  const page = document.querySelector('[data-faq-page]');
  if (!page) return;
  const items = [...page.querySelectorAll('[data-faq-item]')];
  const categories = [...page.querySelectorAll('[data-faq-category]')];
  const search = page.querySelector('[data-faq-search]');
  const clear = page.querySelector('[data-faq-clear]');
  const empty = page.querySelector('[data-faq-empty]');
  const results = page.querySelector('[data-faq-results]');
  const setOpen = (item, open, focus = false) => {
    const question = item.querySelector('[data-faq-question]');
    const answer = item.querySelector('[data-faq-answer]');
    item.classList.toggle('is-open', open);
    question?.setAttribute('aria-expanded', String(open));
    answer?.setAttribute('aria-hidden', String(!open));
    if (focus) question?.focus({ preventScroll: true });
  };
  const filter = () => {
    const term = search?.value.trim().toLowerCase() || '';
    let visible = 0;
    categories.forEach((category) => {
      const categoryText = category.querySelector('h2')?.textContent.toLowerCase() || '';
      let categoryVisible = 0;
      category.querySelectorAll('[data-faq-item]').forEach((item) => {
        const matches = !term || `${categoryText} ${item.textContent}`.toLowerCase().includes(term);
        item.hidden = !matches;
        if (matches) { categoryVisible += 1; visible += 1; }
      });
      category.hidden = categoryVisible === 0;
    });
    if (empty) empty.hidden = visible !== 0;
    if (results) results.textContent = term ? `${visible} matching question${visible === 1 ? '' : 's'}.` : `Showing ${items.length} questions.`;
    if (clear) clear.hidden = !term;
  };
  const focusHash = () => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const item = page.querySelector(`#${CSS.escape(hash)}`)?.closest('[data-faq-item]') || page.querySelector(`[data-faq-item]#${CSS.escape(hash)}`);
    if (item) {
      setOpen(item, true, true);
      window.setTimeout(() => item.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }), 0);
    }
  };
  page.classList.add('faq-enhanced');
  items.forEach((item) => {
    setOpen(item, false);
    item.querySelector('[data-faq-question]')?.addEventListener('click', () => setOpen(item, !item.classList.contains('is-open')));
  });
  search?.addEventListener('input', filter);
  clear?.addEventListener('click', () => { search.value = ''; filter(); search.focus(); });
  window.addEventListener('hashchange', focusHash);
  filter();
  focusHash();
}

function initParallax() {
  const stack = document.querySelector('[data-hero-stack]');
  if (!stack || window.matchMedia('(max-width: 900px), (prefers-reduced-motion: reduce)').matches) return;
  stack.addEventListener('pointermove', (event) => { const r = stack.getBoundingClientRect(); const x = (event.clientX - r.left) / r.width - .5; const y = (event.clientY - r.top) / r.height - .5; stack.querySelectorAll('[data-depth]').forEach((el) => { const d = Number(el.dataset.depth || 0); el.style.translate = `${x * d}px ${y * d}px`; }); });
  stack.addEventListener('pointerleave', () => stack.querySelectorAll('[data-depth]').forEach((el) => { el.style.translate = ''; }));
}

function initResourceFilter() {
  const search = document.querySelector('[data-resource-search]'); const cards = [...document.querySelectorAll('[data-article]')]; const empty = document.querySelector('.article-empty');
  if (!search || !cards.length) return;
  let category = 'all';
  const filter = () => { const term = search.value.trim().toLowerCase(); let visible = 0; cards.forEach((card) => { const ok = (category === 'all' || card.dataset.category === category) && card.textContent.toLowerCase().includes(term); card.hidden = !ok; if (ok) visible += 1; }); empty?.classList.toggle('is-visible', visible === 0); };
  document.querySelectorAll('[data-resource-filter]').forEach((button) => button.addEventListener('click', () => { category = button.dataset.resourceFilter; document.querySelectorAll('[data-resource-filter]').forEach((other) => other.classList.toggle('is-active', other === button)); filter(); }));
  search.addEventListener('input', filter);
}

function initImageDialogs() {
  const controls = document.querySelectorAll('[data-image-dialog]');
  if (!controls.length) return;
  const dialog = document.createElement('dialog');
  dialog.className = 'image-dialog';
  dialog.innerHTML = '<button class="image-dialog-close" type="button" aria-label="Close full screenshot">×</button><img alt="">';
  document.body.append(dialog);
  const image = dialog.querySelector('img');
  const close = () => dialog.close();
  dialog.querySelector('.image-dialog-close').addEventListener('click', close);
  dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
  controls.forEach((control) => control.addEventListener('click', () => { image.src = control.dataset.imageSrc; image.alt = control.dataset.imageAlt || ''; dialog.showModal(); }));
}

function initContactForm() {
  const form = document.querySelector('[data-contact-form]');
  if (!form) return;
  const status = form.querySelector('[data-contact-status]');
  const success = document.querySelector('[data-contact-success]');
  const submit = form.querySelector('button[type="submit"]');
  const fields = ['name', 'email', 'phone', 'category', 'message', 'consent'];
  const errorText = { name:'Enter your full name.', email:'Enter a valid email address.', phone:'Enter a valid phone number or leave this field blank.', category:'Choose a support category.', message:'Enter a message between 20 and 3,000 characters.', consent:'Please acknowledge how Rankerd will use your details.' };
  const setError = (field, message = '') => { const input = form.elements[field]; const target = form.querySelector(`[data-error-for="${field}"]`); if (!input || !target) return; input.setAttribute('aria-invalid', String(Boolean(message))); target.textContent = message; target.hidden = !message; };
  const validate = () => {
    const data = new FormData(form); const values = Object.fromEntries(data.entries()); let first = null;
    const checks = { name:values.name.trim().length >= 2 && values.name.trim().length <= 120, email:/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim()) && values.email.length <= 190, phone:!values.phone.trim() || /^[0-9+().\s-]{7,24}$/.test(values.phone.trim()), category:Boolean(values.category), message:values.message.trim().length >= 20 && values.message.trim().length <= 3000, consent:data.get('consent') === 'yes' };
    fields.forEach((field) => { const invalid = !checks[field]; setError(field, invalid ? errorText[field] : ''); if (invalid && !first) first = form.elements[field]; });
    return { valid:!first, values, first };
  };
  fields.forEach((field) => form.elements[field]?.addEventListener('input', () => { if (form.elements[field].getAttribute('aria-invalid') === 'true') validate(); }));
  form.addEventListener('submit', async (event) => {
    event.preventDefault(); if (form.dataset.submitting === 'true') return;
    const result = validate(); if (!result.valid) { status.textContent = 'Please correct the highlighted fields.'; result.first.focus(); return; }
    form.dataset.submitting = 'true'; submit.disabled = true; submit.dataset.label = submit.textContent; submit.textContent = 'Sending…'; status.textContent = '';
    try {
      const resultBody = await submitContactRequest(fetch, result.values);
      if (!resultBody.ok) {
        const serverFields = resultBody.body?.error?.fields || {};
        Object.keys(serverFields).forEach((field) => setError(field, errorText[field] || 'Please check this field.'));
        throw new Error(resultBody.body?.error?.code || 'contact_send_failed');
      }
      form.hidden = true; success.hidden = false; success.focus();
    } catch (error) {
      const message = error?.message === 'rate_limited'
        ? 'Please wait a little before sending another message.'
        : 'Please check your connection and try again. If the problem continues, use the alternative contact option shown on this page.';
      status.innerHTML = `<strong>We could not send your message</strong><span>${message}</span>`;
    }
    finally { form.dataset.submitting = 'false'; submit.disabled = false; submit.textContent = submit.dataset.label || 'Send Message'; }
  });
}

function initMalaysiaDemoForms() {
  const forms = document.querySelectorAll('[data-malaysia-demo-form]');
  if (!forms.length) return;
  trackEvent('malaysia_page_viewed');
  const errorText = { name:'Enter your name or leave it blank.', phone:'Enter a valid WhatsApp number, including the country code.', tutor_type:'Choose a tutor type if you want to share it.', consent:'Please agree to receive the requested demo details on WhatsApp.' };
  const validPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.startsWith('60')) return digits.length >= 10 && digits.length <= 12 && digits[2] === '1';
    if (digits.startsWith('0')) return digits.length >= 10 && digits.length <= 11 && digits[1] === '1';
    return false;
  };
  forms.forEach((form) => {
    const status = form.querySelector('[data-malaysia-status]');
    const success = form.querySelector('[data-malaysia-success]');
    const submit = form.querySelector('button[type="submit"]');
    const fields = ['name', 'phone', 'tutor_type', 'consent'];
    let phoneFocused = false;
    const setError = (field, message = '') => {
      const input = form.elements[field];
      const target = form.querySelector(`[data-error-for="${field}"]`);
      if (!input || !target) return;
      input.setAttribute('aria-invalid', String(Boolean(message)));
      target.textContent = message;
      target.hidden = !message;
    };
    const validate = () => {
      const data = new FormData(form);
      const values = Object.fromEntries(data.entries());
      const checks = {
        name: typeof values.name !== 'string' || values.name.trim().length <= 120,
        phone: validPhone(values.phone.trim()),
        tutor_type: true,
        consent: data.get('consent') === 'yes',
      };
      let first = null;
      fields.forEach((field) => {
        const invalid = !checks[field];
        setError(field, invalid ? errorText[field] : '');
        if (invalid && !first) first = form.elements[field];
      });
      return { valid: !first, values, first };
    };
    form.addEventListener('focusin', (event) => {
      if (event.target.name === 'phone' && !phoneFocused) { phoneFocused = true; trackEvent('malaysia_hero_whatsapp_form_focused'); }
    });
    fields.forEach((field) => form.elements[field]?.addEventListener('input', () => {
      if (form.elements[field].getAttribute('aria-invalid') === 'true') validate();
    }));
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (form.dataset.submitting === 'true') return;
      const result = validate();
      if (!result.valid) { status.textContent = 'Please correct the highlighted fields.'; result.first.focus(); return; }
      form.dataset.submitting = 'true';
      submit.disabled = true;
      submit.dataset.label = submit.textContent;
      submit.textContent = 'Sending…';
      status.textContent = '';
      trackEvent('malaysia_demo_form_submitted');
      try {
        const response = await fetch('/api/v1/malaysia/demo.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ name: typeof result.values.name === 'string' ? result.values.name.trim() : '', phone: result.values.phone.trim(), tutor_type: result.values.tutor_type || '', consent: result.values.consent === 'yes', website_url: result.values.website_url || '', source: `rankerd_malaysia_${form.dataset.location || 'demo'}` }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok || body.success !== true) {
          const serverFields = body?.error?.fields || {};
          Object.keys(serverFields).forEach((field) => setError(field, errorText[field] || 'Please check this field.'));
          throw new Error('malaysia_demo_failed');
        }
        form.hidden = true;
        success.hidden = false;
        success.focus();
        trackEvent('malaysia_demo_form_succeeded');
      } catch (_error) {
        status.innerHTML = '<strong>We could not submit your request</strong><span>Please check your connection and try again. You can also contact Rankerd using the WhatsApp option below.</span>';
        trackEvent('malaysia_demo_form_failed');
      } finally {
        form.dataset.submitting = 'false';
        submit.disabled = false;
        submit.textContent = submit.dataset.label || 'Send Demo to My WhatsApp';
      }
    });
  });
  document.querySelectorAll('[data-malaysia-event]').forEach((link) => link.addEventListener('click', () => trackEvent(link.dataset.malaysiaEvent)));
  document.querySelectorAll('[data-focus-malaysia-demo]').forEach((link) => link.addEventListener('click', (event) => {
    event.preventDefault();
    const form = document.querySelector('#demo [data-malaysia-demo-form]');
    form?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => form?.elements.phone?.focus(), 360);
  }));
}

function initTryDemo() {
  const form = document.querySelector('[data-try-form]');
  if (!form) return;
  const query = new URLSearchParams(window.location.search);
  const requestedCountry = query.get('country')?.trim().toLowerCase() || '';
  const requestedSource = query.get('source')?.trim().toLowerCase() || '';
  const countryContext = COUNTRY_PAGES_BY_SLUG[requestedCountry];
  const validCountryContext = countryContext && requestedSource === 'country-page' ? countryContext : null;
  const tryCountry = validCountryContext?.slug || '';
  const trySource = validCountryContext && requestedSource ? requestedSource : '';
  if (validCountryContext) {
    document.querySelector('[data-try-eyebrow]')?.replaceChildren(validCountryContext.tryEyebrow);
    document.querySelector('[data-try-title]')?.replaceChildren(validCountryContext.tryTitle);
    document.querySelector('[data-try-copy]')?.replaceChildren(validCountryContext.tryCopy);
    document.querySelector('[data-try-workspace-title]')?.replaceChildren(validCountryContext.tryWorkspaceTitle);
    document.querySelector('[data-try-workspace-note]')?.replaceChildren(validCountryContext.tryWorkspaceNote);
    document.querySelector('[data-try-subject-help]')?.replaceChildren(validCountryContext.trySubjectHelp);
    const returnLink = document.querySelector('[data-try-country-return]');
    if (returnLink) { returnLink.hidden = false; returnLink.textContent = validCountryContext.returnLabel; returnLink.href = validCountryContext.returnHref; }
  }
  const fileInput = form.querySelector('[data-try-file]');
  const subjectInput = form.querySelector('[data-try-subject]');
  const submit = form.querySelector('[data-try-submit]');
  const status = form.querySelector('[data-try-status]');
  const progress = document.querySelector('[data-try-progress]');
  const results = document.querySelector('[data-try-results]');
  const progressBar = document.querySelector('[data-try-progress-bar]');
  const progressCount = document.querySelector('[data-try-progress-count]');
  const questionList = document.querySelector('[data-try-question-list]');
  const resultSubtitle = document.querySelector('[data-try-results-subtitle]');
  const resultTitle = document.querySelector('[data-try-results-title]');
  const resultBadge = document.querySelector('[data-try-results-badge]');
  const sharePanel = document.querySelector('[data-try-share-panel]');
  const copyButton = document.querySelector('[data-try-copy]');
  const whatsappButton = document.querySelector('[data-try-whatsapp]');
  const shareStatus = document.querySelector('[data-try-share-status]');
  const fileTitle = form.querySelector('[data-try-file-title]');
  const fileMeta = form.querySelector('[data-try-file-meta]');
  const dropzone = form.querySelector('[data-try-dropzone]');
  const bootstrapEndpoint = '/api/public_trial/demo_bootstrap.php';
  const createEndpoint = '/api/public_trial/demo_create.php';
  const statusEndpoint = '/api/public_trial/demo_status.php';
  let csrf = '';
  let maxBytes = 10 * 1024 * 1024;
  let allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
  let pollTimer = null;
  let pollStartedAt = 0;
  let consecutivePollErrors = 0;
  let lastProgress = 0;
  let lastStageIndex = -1;
  let pollDelay = 900;

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;' }[character]));
  const trackTry = (name, properties = {}) => {
    const contextProperties = tryCountry ? { country: tryCountry, source: trySource || undefined, ...properties } : properties;
    if (typeof window.rankerdAnalytics?.track === 'function') window.rankerdAnalytics.track(name, contextProperties);
    window.dispatchEvent(new CustomEvent('rankerd:analytics', { detail: { name, properties: contextProperties } }));
    fetch('/api/public/index.php?route=v1/analytics/visitor-event', { method:'POST', headers:{ 'Content-Type':'application/json' }, body:JSON.stringify({ event_name:name, page_path:'/try/', properties:contextProperties }), keepalive:true }).catch(() => {});
  };
  const errorFor = (field) => form.querySelector(`[data-try-error="${field}"]`);
  const setError = (field, message = '') => {
    const target = errorFor(field);
    const control = field === 'material' ? fileInput : subjectInput;
    if (target) target.textContent = message;
    if (control) control.setAttribute('aria-invalid', String(Boolean(message)));
  };
  const fileExtension = (file) => String(file?.name || '').toLowerCase().split('.').pop() || '';
  const validFile = (file) => Boolean(file && (allowedExtensions.includes(fileExtension(file)) || ['application/pdf','image/jpeg','image/png','image/webp'].includes(file.type)));
  const validate = () => {
    const file = fileInput.files?.[0];
    const typeError = file && !validFile(file) ? 'Upload a JPEG, PNG, WebP image or PDF.' : '';
    const sizeError = file && file.size > maxBytes ? `That file is too large. Upload up to ${Math.round(maxBytes / 1024 / 1024)} MB.` : '';
    setError('material', typeError || sizeError);
    setError('subject', subjectInput.value ? '' : 'Choose the subject for this material.');
    const ready = Boolean(file && !typeError && !sizeError && subjectInput.value && csrf);
    submit.disabled = !ready;
    return ready;
  };
  const setFile = (file) => {
    if (!file) return;
    try { const transfer = new DataTransfer(); transfer.items.add(file); fileInput.files = transfer.files; } catch (_) {}
    fileTitle.textContent = file.name;
    fileMeta.textContent = `${(file.size / 1024 / 1024).toFixed(1)} MB · Ready to process temporarily`;
    trackTry('try_upload_completed', { file_type: file.type || fileExtension(file) });
    validate();
  };
  const setStage = (stage, progressValue) => {
    const value = Math.max(lastProgress, Math.min(100, Number(progressValue || 0)));
    lastProgress = value;
    progressBar.style.width = `${value}%`;
    progressCount.textContent = `${value}%`;
    const steps = [...document.querySelectorAll('[data-try-step]')];
    const activeIndex = Math.max(lastStageIndex, steps.findIndex((step) => step.dataset.tryStep === stage));
    lastStageIndex = activeIndex;
    steps.forEach((step, index) => { step.classList.toggle('is-active', index === activeIndex); step.classList.toggle('is-complete', activeIndex >= 0 && index < activeIndex); });
  };
  const providerFailureCodes = new Set(['gemini_empty_response', 'ocr_provider_error', 'ocr_failed', 'provider_failure', 'provider_unavailable', 'provider_timeout', 'provider_rate_limited', 'gemini_transport_error', 'provider_transport', 'provider_transport_error', 'ocr_transport_error', 'provider_error']);
  const friendlyError = (payload, status = 0) => {
    if (payload?.error === 'daily_limit_reached') return 'You’ve used today’s free demo.';
    if (payload?.error === 'file_too_large' || payload?.error === 'invalid_upload_size' || payload?.error === 'payload_too_large' || payload?.error === 'http_413' || status === 413) {
      return payload?.message || 'That file is too large. Please upload a file up to 10 MB.';
    }
    if (payload?.message) return payload.message;
    if (providerFailureCodes.has(payload?.error)) return "We couldn't process this material right now. Please try again.";
    return 'We could not create the recap. Please try a clearer image or PDF.';
  };
  const showLimit = (message) => {
    progress.hidden = true;
    results.hidden = false;
    results.querySelector('.try-results-heading').hidden = true;
    results.querySelector('.try-conversion').hidden = true;
    results.querySelector('.try-no-save').hidden = true;
    questionList.innerHTML = `<div class="try-limit"><p class="site-eyebrow">TODAY’S DEMO IS USED</p><h2>${escapeHtml(message || 'You’ve used today’s free demo.')}</h2><p>Start your 14-day free trial to create, edit and share more assignments.</p><a class="site-btn" href="https://www.rankerd.com/app/#/login" data-try-signup>Start 14-day free trial <span aria-hidden="true">→</span></a></div>`;
    results.scrollIntoView({ behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block:'start' });
    trackTry('try_rate_limited');
  };
  const renderResults = (payload) => {
    const c = Array.isArray(payload.questions) ? payload.questions.slice(0, 5) : [];
    const p = Number(payload.question_count);
    const questions = c;
    const questionCount = p;
    if (payload.status !== 'succeeded' || payload.validation_profile !== 'public_try_preview_relaxed' || ![4, 5].includes(questionCount) || questions.length !== questionCount || Number(payload.required_question_count) !== 5 || Boolean(payload.partial_preview) !== (questionCount === 4)) throw new Error('We could not prepare enough clean questions. Please try another page.');
    const partialPreview = questionCount === 4;
    const resultCountry = COUNTRY_PAGES_BY_SLUG[String(payload.country || '').trim().toLowerCase()];
    const resultReturnLink = document.querySelector('[data-try-country-return]');
    if (resultCountry && resultReturnLink) {
      resultReturnLink.hidden = false;
      resultReturnLink.textContent = resultCountry.returnLabel;
      resultReturnLink.href = resultCountry.returnHref;
    }
    resultTitle.textContent = partialPreview ? 'Four questions to review.' : 'Five questions to review.';
    resultBadge.textContent = `${questionCount} questions`;
    resultSubtitle.textContent = partialPreview
      ? 'We generated 4 strong preview questions from your material.'
      : `${payload.subject || 'Your subject'} · Review the draft before you decide what to share.`;
    if (sharePanel) sharePanel.hidden = false;
    const shareUrl = String(payload.share_url || '').trim();
    const shareText = String(payload.share_text || '').trim();
    const suppliedWhatsappShareUrl = String(payload.whatsapp_share_url || '').trim();
    const safeShareText = shareUrl && shareText.includes(shareUrl)
      ? shareText
      : `Try this Rankerd practice test: ${shareUrl}`;
    const encodedShareText = encodeURIComponent(safeShareText);
    const whatsappShareUrl = shareUrl && suppliedWhatsappShareUrl.startsWith('https://wa.me/?text=')
      ? suppliedWhatsappShareUrl
      : shareUrl ? `https://wa.me/?text=${encodedShareText}` : '';
    const whatsappFallbackUrl = whatsappShareUrl.startsWith('https://wa.me/?text=')
      ? `https://api.whatsapp.com/send?text=${whatsappShareUrl.slice('https://wa.me/?text='.length)}`
      : '';
    if (copyButton) {
      copyButton.disabled = !shareUrl;
      copyButton.onclick = async () => {
        if (!shareUrl) return;
        try {
          if (!navigator.clipboard?.writeText) throw new Error('clipboard_unavailable');
          await navigator.clipboard.writeText(shareUrl);
          if (shareStatus) shareStatus.textContent = 'Test link copied. It expires automatically after 24 hours.';
          trackTry('try_share_link_copied');
        } catch (_) {
          const prompted = window.prompt('Copy test link', shareUrl);
          if (shareStatus) shareStatus.textContent = prompted === null
            ? 'Copy was cancelled. Select and copy the link shown in the dialog.'
            : 'Test link ready to copy. It expires automatically after 24 hours.';
          trackTry('try_share_link_copy_failed');
        }
      };
    }
    if (whatsappButton) {
      whatsappButton.disabled = !whatsappShareUrl;
      whatsappButton.onclick = () => {
        if (!whatsappShareUrl) return;
        const opened = window.open(whatsappShareUrl, '_blank', 'noopener,noreferrer');
        if (!opened && whatsappFallbackUrl) {
          window.open(whatsappFallbackUrl, '_blank', 'noopener,noreferrer');
        }
        trackTry('trial_whatsapp_clicked', { question_count: questions.length });
      };
    }
    if (shareStatus && (!shareUrl || !whatsappShareUrl)) {
      shareStatus.textContent = !shareUrl
        ? 'Sharing is temporarily unavailable. Please reload the result or start a Rankerd trial.'
        : 'WhatsApp sharing is temporarily unavailable. Use “Copy test link” to share the test manually.';
    }
    questionList.innerHTML = questions.map((question, index) => {
      const options = Array.isArray(question.options) ? question.options : [];
      const correctIndex = Number(question.correct_index);
      const optionsHtml = options.map((option, optionIndex) => `<li class="try-option${optionIndex === correctIndex ? ' is-correct' : ''}"><span>${String.fromCharCode(65 + optionIndex)}.</span> ${escapeHtml(option)}</li>`).join('');
      return `<article class="try-question"><div class="try-question-top"><span class="try-question-number">Question ${index + 1}</span><span>Draft to review</span></div><p class="try-question-prompt">${escapeHtml(question.question)}</p><ul class="try-options">${optionsHtml}</ul><div class="try-answer"><strong>Correct answer: ${escapeHtml(question.correct_answer || options[correctIndex] || '')}</strong>${question.explanation ? `<span class="try-explanation">${escapeHtml(question.explanation)}</span>` : ''}</div></article>`;
    }).join('');
    progress.hidden = true;
    results.hidden = false;
    results.scrollIntoView({ behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block:'start' });
    trackTry('try_generation_completed', { question_count:questions.length });
    results.querySelectorAll('[data-try-signup]').forEach((link) => link.addEventListener('click', () => trackTry('try_signup_clicked')));
  };
  const resetForm = () => {
    if (pollTimer) window.clearTimeout(pollTimer);
    form.reset();
    fileTitle.textContent = 'Choose a worksheet, notebook photo or PDF';
    fileMeta.textContent = `${allowedExtensions.map((extension) => extension.toUpperCase()).join(', ')} · up to ${Math.round(maxBytes / 1024 / 1024)} MB`;
    status.textContent = '';
    setError('material');
    setError('subject');
    results.hidden = true;
    form.hidden = false;
    submit.disabled = true;
  };
  const fail = (message) => {
    if (pollTimer) window.clearTimeout(pollTimer);
    progress.hidden = true;
    form.hidden = false;
    status.innerHTML = `<strong>${escapeHtml(message)}</strong><span>Try a clearer or longer upload, or continue with the full product.</span><span class="try-failure-actions"><button type="button" class="text-link" data-try-reset>Upload another page</button><a class="text-link" href="https://www.rankerd.com/app/#/login">Start 14-day free trial →</a></span>`;
    submit.disabled = false;
  };
  const poll = async (token) => {
    try {
      const response = await fetch(`${statusEndpoint}?token=${encodeURIComponent(token)}`, { headers:{ Accept:'application/json' } });
      const payload = await response.json().catch(() => ({}));
      consecutivePollErrors = 0;
      pollDelay = 900;
      if (payload.error === 'daily_limit_reached') { showLimit(payload.message); return; }
      if (!response.ok) { fail(friendlyError(payload)); return; }
      setStage(payload.stage, payload.progress);
      if (payload.status === 'failed') { fail(friendlyError(payload)); trackTry('try_generation_failed', { error_code:payload.error || 'generation_failed' }); return; }
      if (payload.status === 'succeeded') { renderResults(payload); return; }
      if (Date.now() - pollStartedAt > 180000) { fail('This is taking longer than expected. Please try again with a shorter or clearer upload.'); trackTry('try_generation_failed', { error_code:'timeout' }); return; }
      pollTimer = window.setTimeout(() => poll(token), pollDelay);
    } catch (_) {
      consecutivePollErrors += 1;
      pollDelay = Math.min(4000, Math.round(Math.max(1200, pollDelay * 1.6)));
      if (consecutivePollErrors >= 8 || Date.now() - pollStartedAt > 180000) { fail('We lost the connection while preparing your recap. Please try again.'); trackTry('try_generation_failed', { error_code:'status_unavailable' }); return; }
      pollTimer = window.setTimeout(() => poll(token), pollDelay);
    }
  };
  fileInput.addEventListener('change', () => setFile(fileInput.files?.[0]));
  subjectInput.addEventListener('change', validate);
  ['dragenter','dragover'].forEach((eventName) => dropzone.addEventListener(eventName, (event) => { event.preventDefault(); dropzone.classList.add('is-dragging'); }));
  ['dragleave','drop'].forEach((eventName) => dropzone.addEventListener(eventName, (event) => { event.preventDefault(); dropzone.classList.remove('is-dragging'); }));
  dropzone.addEventListener('drop', (event) => setFile(event.dataTransfer.files?.[0]));
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    status.textContent = '';
    if (!validate()) return;
    submit.disabled = true;
    form.hidden = true;
    progress.hidden = false;
    results.hidden = true;
    lastProgress = 0;
    lastStageIndex = -1;
    pollDelay = 900;
    setStage('reading_material', 5);
    pollStartedAt = Date.now();
    consecutivePollErrors = 0;
    trackTry('try_generation_started', { subject:subjectInput.value });
    try {
      const body = new FormData();
      body.append('material', fileInput.files[0]);
      body.append('subject', subjectInput.value);
      if (tryCountry) body.append('country', tryCountry);
      if (trySource) body.append('source', trySource);
      const response = await fetch(createEndpoint, { method:'POST', headers:{ 'X-Rankerd-Try-CSRF':csrf, Accept:'application/json' }, body });
      const payload = await response.json().catch(() => ({}));
      if (payload.error === 'daily_limit_reached') { showLimit(payload.message); return; }
      if (!response.ok || !payload.job_token) {
        fail(friendlyError(payload, response.status));
        trackTry('try_generation_failed', {
          error_code: payload.error || (response.status === 413 ? 'http_413' : `http_${response.status}`),
          status_code: response.status,
          request_id: payload.request_id || null,
        });
        return;
      }
      poll(payload.job_token);
    } catch (error) {
      fail(error.message || 'We could not start the demo. Please try again.');
      trackTry('try_generation_failed', {
        error_code: 'network_error',
        status_code: 0,
        request_id: null,
      });
    }
  });
  document.addEventListener('click', (event) => { if (event.target.closest?.('[data-try-reset]')) resetForm(); });
  document.addEventListener('click', (event) => { const signup = event.target.closest?.('[data-try-signup]'); if (signup) trackTry('try_signup_clicked'); });
  trackTry('try_view');
  fetch(bootstrapEndpoint, { headers:{ Accept:'application/json' } }).then((response) => response.json()).then((payload) => {
    if (!payload.ok || !payload.csrf) { status.textContent = friendlyError(payload); return; }
    csrf = payload.csrf;
    maxBytes = Math.max(1, Number(payload.max_upload_mb || 10)) * 1024 * 1024;
    allowedExtensions = Array.isArray(payload.allowed_extensions) ? payload.allowed_extensions.map((extension) => String(extension).toLowerCase()) : allowedExtensions;
    fileMeta.textContent = `${allowedExtensions.map((extension) => extension.toUpperCase()).join(', ')} · up to ${Math.round(maxBytes / 1024 / 1024)} MB`;
    if (Array.isArray(payload.subjects)) {
      const subjects = validCountryContext
        ? [...payload.subjects].sort((a, b) => {
          const aIndex = validCountryContext.subjectOrder.indexOf(a);
          const bIndex = validCountryContext.subjectOrder.indexOf(b);
          return (aIndex < 0 ? validCountryContext.subjectOrder.length : aIndex) - (bIndex < 0 ? validCountryContext.subjectOrder.length : bIndex);
        })
        : payload.subjects;
      subjectInput.innerHTML = '<option value="">Choose a subject</option>' + subjects.map((subject) => `<option value="${escapeHtml(subject)}">${escapeHtml(subject)}</option>`).join('');
    }
    validate();
  }).catch(() => { status.textContent = 'The demo is temporarily unavailable. Please refresh and try again.'; });
}

function initTemplateSignup() {
  const form = document.querySelector('[data-template-signup]');
  if (!form) return;
  const email = form.elements.email;
  const error = form.querySelector('[data-template-error]');
  const status = form.querySelector('[data-template-status]');
  const submit = form.querySelector('button[type="submit"]');
  const success = document.querySelector('[data-template-success]');
  const utm = new URLSearchParams(window.location.search);
  const attribution = Object.fromEntries(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].map((key) => [key, utm.get(key) || '']));
  let started = false;
  const showError = (message = '') => { error.textContent = message; email.setAttribute('aria-invalid', String(Boolean(message))); };
  const validate = () => {
    const value = email.value.trim();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && value.length <= 190;
    showError(valid ? '' : 'Enter a valid email address.');
    return valid;
  };
  email.addEventListener('focus', () => { if (!started) { started = true; templateAnalytics('template_form_started'); } }, { once: true });
  email.addEventListener('input', () => { if (email.getAttribute('aria-invalid') === 'true') validate(); });
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (form.dataset.submitting === 'true') return;
    if (!validate()) { email.focus(); return; }
    form.dataset.submitting = 'true';
    submit.disabled = true;
    submit.dataset.label = submit.textContent;
    submit.textContent = 'Preparing your templates…';
    status.textContent = '';
    try {
      const result = await submitTemplateSignup(fetch, { email: email.value, website_url: form.elements.website_url.value, source: 'rankerd_templates', landing_page: window.location.pathname, referrer: document.referrer, ...attribution });
      if (!result.ok) {
        const message = result.body?.error?.fields?.email?.[0] === 'invalid_email' ? 'Enter a valid email address.' : result.body?.error?.message || 'We could not send the templates right now. Please try again.';
        if (result.response.status === 422) showError(message);
        else status.textContent = message;
        templateAnalytics('template_delivery_failed', { error_code: result.body?.error?.code || 'request_failed' });
        return;
      }
      form.hidden = true;
      success.hidden = false;
      success.focus();
      templateAnalytics('templates_lead_submitted');
      templateAnalytics('template_delivery_success', { duplicate: result.body?.duplicate ? 1 : 0 });
    } catch (_) {
      status.textContent = 'We could not send the templates right now. Please check your connection and try again.';
      templateAnalytics('template_delivery_failed', { error_code: 'network_error' });
    } finally {
      form.dataset.submitting = 'false';
      submit.disabled = false;
      submit.textContent = submit.dataset.label || 'Get the 10 free templates →';
    }
  });
  document.querySelectorAll('[data-template-trial]').forEach((link) => link.addEventListener('click', () => templateAnalytics('templates_trial_clicked', { source:'templates', placement:'success_cta' })));
  document.querySelectorAll('[data-template-focus]').forEach((link) => link.addEventListener('click', (event) => { event.preventDefault(); email.focus({ preventScroll: true }); form.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' }); }));
  templateAnalytics('templates_viewed');
}

document.addEventListener('DOMContentLoaded', () => { document.documentElement.classList.add('has-motion'); mountSharedChrome(); initMotionReveal(); initSpeedProof(); initTabs(); initFaqs(); initFaqPage(); initParallax(); initResourceFilter(); initImageDialogs(); initContactForm(); initLegalNavigation(); initTryDemo(); initTemplateSignup(); initMarketingAnalytics(); document.querySelectorAll('[data-homepage-template-cta]').forEach((link) => link.addEventListener('click', () => marketingAnalytics('homepage_template_cta_clicked', { source:'homepage', placement:'resource_cta' }))); });
